import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FindingScreen } from './FindingScreen';
import { NextPulseScreen } from './NextPulseScreen';
import { ResultsCover } from './ResultsCover';
import { ResultsHeader } from './ResultsHeader';
import { SignUpScreen } from './SignUpScreen';
import { chapters } from '../../data/chapters';
import { findings } from '../../data/results';
type ResultsStep = {
  kind: 'cover';
} | {
  kind: 'finding';
  index: number;
} | {
  kind: 'next';
} | {
  kind: 'signup';
};
const STEPS: ResultsStep[] = [{
  kind: 'cover'
}, ...findings.map((_, index): ResultsStep => ({
  kind: 'finding',
  index
})), {
  kind: 'next'
}, {
  kind: 'signup'
}];
const variants = {
  enter: (d: number) => ({
    opacity: 0,
    x: d * 40
  }),
  center: {
    opacity: 1,
    x: 0
  },
  exit: (d: number) => ({
    opacity: 0,
    x: d * -40
  })
};
interface ResultsExperienceProps {
  /** Auto-plays between the first two screens (used for campaign previews). */
  previewLoop?: boolean;
}
export function ResultsExperience({
  previewLoop = false
}: ResultsExperienceProps) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [interpretations, setInterpretations] = useState<Record<string, string>>({});
  const [nextChoice, setNextChoice] = useState<string | null>(null);
  const step = STEPS[index];
  const go = (delta: 1 | -1) => {
    setDirection(delta);
    setIndex(i => Math.min(Math.max(i + delta, 0), STEPS.length - 1));
  };
  useEffect(() => {
    if (!previewLoop) return;
    const t = window.setTimeout(() => {
      const delta = index === 0 ? 1 : -1;
      setDirection(delta);
      setIndex(index === 0 ? 1 : 0);
    }, 3400);
    return () => window.clearTimeout(t);
  }, [previewLoop, index]);
  const next = () => go(1);
  const restart = () => {
    setDirection(-1);
    setInterpretations({});
    setNextChoice(null);
    setIndex(0);
  };
  let screen: React.ReactNode;
  if (step.kind === 'cover') {
    screen = <ResultsCover onBegin={next} data-id="element-667" data-mp="1k7k2wm6gdeq8" />;
  } else if (step.kind === 'finding') {
    const finding = findings[step.index];
    const chapter = chapters.find(c => c.id === finding.chapterId) ?? chapters[step.index];
    screen = <FindingScreen chapter={chapter} finding={finding} total={findings.length} selected={interpretations[finding.id] ?? null} isLast={step.index === findings.length - 1} onInterpret={id => setInterpretations(prev => ({
      ...prev,
      [finding.id]: id
    }))} onContinue={next} data-id="element-668" data-mp="1t6xizxp311jx" />;
  } else if (step.kind === 'next') {
    screen = <NextPulseScreen interpretations={interpretations} selected={nextChoice} onSelect={setNextChoice} onContinue={next} data-id="element-669" data-mp="1agfp3psijzwz" />;
  } else {
    screen = <SignUpScreen onRestart={restart} data-id="element-670" data-mp="hrilmssyo7oa" />;
  }
  return <div className="relative h-full w-full overflow-hidden bg-white text-navy-900" data-id="element-671" data-mp="mrmqtmof5fz7">
      {step.kind !== 'cover' && <ResultsHeader step={index} total={STEPS.length - 1} onBack={() => go(-1)} data-id="element-672" data-mp="f7ub79zmkhby" />}
      <AnimatePresence mode="wait" custom={direction} initial={false} data-id="element-673" data-mp="a9ssiq642k10">
        <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
        duration: 0.45,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0" data-id="element-674" data-mp="5aplj06km3qe">
          {screen}
        </motion.div>
      </AnimatePresence>
    </div>;
}